/**
 * Social Feed Preview Mockups
 *
 * Pixel-accurate mockups of how a blog post link preview will render
 * in the feeds of X (Twitter), Facebook, LinkedIn, and Pinterest.
 * Updates live as the author fills in social sharing fields.
 */

import React, { useState } from 'react';

export interface SocialPreviewData {
  title: string;
  socialTitle: string;
  excerpt: string;
  socialDescription: string;
  socialImageUrl: string;
  heroImageUrl: string;
  slug: string;
  author: string;
  // X / Twitter overrides
  twitterCardType: string;
  twitterTitle: string;
  twitterDescription: string;
  twitterImageUrl: string;
  // Pinterest
  pinterestImageUrl: string;
}

type Platform = 'x' | 'facebook' | 'linkedin' | 'pinterest';

const PLATFORM_TABS: { id: Platform; label: string; color: string }[] = [
  { id: 'x', label: 'X', color: '#000000' },
  { id: 'facebook', label: 'Facebook', color: '#1877F2' },
  { id: 'linkedin', label: 'LinkedIn', color: '#0A66C2' },
  { id: 'pinterest', label: 'Pinterest', color: '#E60023' },
];

// ── Resolved values with fallback chains ──

function resolve(data: SocialPreviewData) {
  const ogTitle = data.socialTitle || data.title || 'Untitled Post';
  const ogDesc = data.socialDescription || data.excerpt || '';
  const ogImage = data.socialImageUrl || data.heroImageUrl || '';
  const xTitle = data.twitterTitle || ogTitle;
  const xDesc = data.twitterDescription || ogDesc;
  const xImage = data.twitterImageUrl || ogImage;
  const pinImage = data.pinterestImageUrl || ogImage;
  const domain = 'coachcedar.com';
  const fullUrl = `${domain}/blog/${data.slug || 'untitled'}`;
  return { ogTitle, ogDesc, ogImage, xTitle, xDesc, xImage, pinImage, domain, fullUrl };
}

function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return text.slice(0, max - 1).trimEnd() + '\u2026';
}

// ── Placeholder Image ──

const PlaceholderImage: React.FC<{ width: string; height: string; ratio?: string }> = ({ width, height, ratio }) => (
  <div style={{
    width, height, aspectRatio: ratio,
    background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 50%, #d1d5db 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#9ca3af', fontSize: '11px', fontWeight: 500,
  }}>
    No image set
  </div>
);

// ── X / Twitter Preview ──

const XPreview: React.FC<{ data: SocialPreviewData }> = ({ data }) => {
  const r = resolve(data);
  const isLargeImage = data.twitterCardType !== 'summary';

  if (isLargeImage) {
    // summary_large_image — big image on top, metadata below
    return (
      <div style={{
        border: '1px solid #2f3336', borderRadius: '16px', overflow: 'hidden',
        background: '#000', maxWidth: '504px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}>
        {r.xImage ? (
          <img src={r.xImage} alt="" style={{ width: '100%', height: '252px', objectFit: 'cover', display: 'block' }} />
        ) : (
          <PlaceholderImage width="100%" height="252px" />
        )}
        <div style={{ padding: '12px 14px' }}>
          <div style={{ fontSize: '13px', color: '#71767b', lineHeight: 1.2 }}>
            {r.domain}
          </div>
          <div style={{ fontSize: '15px', color: '#e7e9ea', fontWeight: 400, lineHeight: 1.3, marginTop: '2px' }}>
            {truncate(r.xTitle, 70)}
          </div>
          <div style={{ fontSize: '15px', color: '#71767b', lineHeight: 1.3, marginTop: '2px' }}>
            {truncate(r.xDesc, 125)}
          </div>
        </div>
      </div>
    );
  }

  // summary — small thumbnail on left
  return (
    <div style={{
      border: '1px solid #2f3336', borderRadius: '16px', overflow: 'hidden',
      background: '#000', maxWidth: '504px', display: 'flex',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      {r.xImage ? (
        <img src={r.xImage} alt="" style={{ width: '130px', height: '130px', objectFit: 'cover', flexShrink: 0 }} />
      ) : (
        <div style={{ width: '130px', height: '130px', flexShrink: 0 }}>
          <PlaceholderImage width="100%" height="100%" />
        </div>
      )}
      <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }}>
        <div style={{ fontSize: '13px', color: '#71767b', lineHeight: 1.2 }}>{r.domain}</div>
        <div style={{ fontSize: '15px', color: '#e7e9ea', fontWeight: 400, lineHeight: 1.3, marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {truncate(r.xTitle, 70)}
        </div>
        <div style={{ fontSize: '15px', color: '#71767b', lineHeight: 1.3, marginTop: '2px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {truncate(r.xDesc, 125)}
        </div>
      </div>
    </div>
  );
};

// ── Facebook Preview ──

const FacebookPreview: React.FC<{ data: SocialPreviewData }> = ({ data }) => {
  const r = resolve(data);
  return (
    <div style={{
      border: '1px solid #dadde1', borderRadius: '8px', overflow: 'hidden',
      background: '#fff', maxWidth: '504px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    }}>
      {r.ogImage ? (
        <img src={r.ogImage} alt="" style={{ width: '100%', height: '264px', objectFit: 'cover', display: 'block' }} />
      ) : (
        <PlaceholderImage width="100%" height="264px" />
      )}
      <div style={{ padding: '10px 12px', background: '#f2f3f5', borderTop: '1px solid #dadde1' }}>
        <div style={{ fontSize: '12px', color: '#606770', textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: 1.3 }}>
          {r.domain}
        </div>
        <div style={{
          fontSize: '16px', fontWeight: 600, color: '#1d2129', lineHeight: 1.35,
          marginTop: '3px',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>
          {truncate(r.ogTitle, 65)}
        </div>
        <div style={{
          fontSize: '14px', color: '#606770', lineHeight: 1.35, marginTop: '3px',
          display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>
          {truncate(r.ogDesc, 110)}
        </div>
      </div>
    </div>
  );
};

// ── LinkedIn Preview ──

const LinkedInPreview: React.FC<{ data: SocialPreviewData }> = ({ data }) => {
  const r = resolve(data);
  return (
    <div style={{
      border: '1px solid #e0dfdc', borderRadius: '8px', overflow: 'hidden',
      background: '#fff', maxWidth: '504px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    }}>
      {r.ogImage ? (
        <img src={r.ogImage} alt="" style={{ width: '100%', height: '264px', objectFit: 'cover', display: 'block' }} />
      ) : (
        <PlaceholderImage width="100%" height="264px" />
      )}
      <div style={{ padding: '8px 12px 12px', background: '#fff' }}>
        <div style={{
          fontSize: '14px', fontWeight: 600, color: 'rgba(0,0,0,0.9)', lineHeight: 1.4,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>
          {truncate(r.ogTitle, 70)}
        </div>
        <div style={{ fontSize: '12px', color: 'rgba(0,0,0,0.6)', marginTop: '4px', lineHeight: 1.3 }}>
          {r.domain}
        </div>
      </div>
    </div>
  );
};

// ── Pinterest Preview ──

const PinterestPreview: React.FC<{ data: SocialPreviewData }> = ({ data }) => {
  const r = resolve(data);
  const pinImg = r.pinImage;
  return (
    <div style={{
      borderRadius: '16px', overflow: 'hidden',
      background: '#fff', maxWidth: '236px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    }}>
      {pinImg ? (
        <img src={pinImg} alt="" style={{ width: '100%', aspectRatio: '2/3', objectFit: 'cover', display: 'block' }} />
      ) : (
        <PlaceholderImage width="100%" height="auto" ratio="2/3" />
      )}
      <div style={{ padding: '8px 8px 12px' }}>
        <div style={{
          fontSize: '14px', fontWeight: 600, color: '#111', lineHeight: 1.35,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>
          {truncate(r.ogTitle, 100)}
        </div>
        {r.ogDesc && (
          <div style={{
            fontSize: '12px', color: '#767676', lineHeight: 1.35, marginTop: '4px',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {truncate(r.ogDesc, 80)}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px' }}>
          <div style={{
            width: '24px', height: '24px', borderRadius: '50%', background: '#2E5E34',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: '11px', fontWeight: 700,
          }}>C</div>
          <span style={{ fontSize: '12px', fontWeight: 600, color: '#111' }}>Coach Cedar</span>
        </div>
      </div>
    </div>
  );
};

// ── Main Component ──

interface SocialPreviewsProps {
  data: SocialPreviewData;
}

export const SocialPreviews: React.FC<SocialPreviewsProps> = ({ data }) => {
  const [activePlatform, setActivePlatform] = useState<Platform>('x');
  const r = resolve(data);

  // Per-platform quality checks
  const getIssues = (platform: Platform): string[] => {
    const issues: string[] = [];
    switch (platform) {
      case 'x': {
        const img = data.twitterImageUrl || data.socialImageUrl || data.heroImageUrl;
        if (!img) issues.push('No image \u2014 card will render without a visual');
        const title = data.twitterTitle || data.socialTitle || data.title;
        if (title.length > 70) issues.push('Title exceeds 70 chars \u2014 will be truncated');
        const desc = data.twitterDescription || data.socialDescription || data.excerpt;
        if (!desc) issues.push('No description \u2014 card will show title only');
        if (desc && desc.length > 125) issues.push('Description exceeds 125 chars \u2014 will be truncated');
        break;
      }
      case 'facebook': {
        const img = data.socialImageUrl || data.heroImageUrl;
        if (!img) issues.push('No image \u2014 link preview will be text-only');
        const title = data.socialTitle || data.title;
        if (title.length > 65) issues.push('Title over 65 chars \u2014 may truncate on mobile');
        const desc = data.socialDescription || data.excerpt;
        if (!desc) issues.push('No description set');
        break;
      }
      case 'linkedin': {
        const img = data.socialImageUrl || data.heroImageUrl;
        if (!img) issues.push('No image \u2014 LinkedIn hides imageless link previews in feeds');
        const title = data.socialTitle || data.title;
        if (title.length > 70) issues.push('Title exceeds 70 chars');
        break;
      }
      case 'pinterest': {
        const img = data.pinterestImageUrl || data.socialImageUrl || data.heroImageUrl;
        if (!img) issues.push('No image \u2014 pins require an image');
        if (!data.pinterestImageUrl && (data.socialImageUrl || data.heroImageUrl)) {
          issues.push('Using landscape image \u2014 upload a vertical 1000\u00d71500 pin for best results');
        }
        break;
      }
    }
    return issues;
  };

  const issues = getIssues(activePlatform);

  return (
    <div>
      {/* Platform tabs */}
      <div style={{
        display: 'flex', gap: '2px', marginBottom: '12px',
        background: '#f3f4f6', borderRadius: '8px', padding: '2px',
      }}>
        {PLATFORM_TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActivePlatform(tab.id)}
            style={{
              flex: 1, padding: '5px 0', fontSize: '11px', fontWeight: 600,
              borderRadius: '6px', border: 'none', cursor: 'pointer',
              background: activePlatform === tab.id ? '#fff' : 'transparent',
              color: activePlatform === tab.id ? tab.color : '#6b7280',
              boxShadow: activePlatform === tab.id ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.15s ease',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Preview card */}
      <div style={{
        padding: activePlatform === 'x' ? '16px' : '16px',
        background: activePlatform === 'x' ? '#000' : '#f0f2f5',
        borderRadius: '8px',
        display: 'flex', justifyContent: activePlatform === 'pinterest' ? 'center' : 'flex-start',
      }}>
        {activePlatform === 'x' && <XPreview data={data} />}
        {activePlatform === 'facebook' && <FacebookPreview data={data} />}
        {activePlatform === 'linkedin' && <LinkedInPreview data={data} />}
        {activePlatform === 'pinterest' && <PinterestPreview data={data} />}
      </div>

      {/* Issues / tips */}
      {issues.length > 0 && (
        <div style={{
          marginTop: '8px', padding: '8px 10px',
          background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px',
        }}>
          {issues.map((issue, i) => (
            <div key={i} style={{ fontSize: '11px', color: '#92400e', lineHeight: 1.5, display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
              <span style={{ flexShrink: 0, marginTop: '1px' }}>\u26A0</span>
              <span>{issue}</span>
            </div>
          ))}
        </div>
      )}

      {/* Fallback chain reminder */}
      <div style={{
        marginTop: '8px', fontSize: '10px', color: '#9ca3af', lineHeight: 1.5,
        padding: '6px 8px', background: '#f9fafb', borderRadius: '4px',
      }}>
        {activePlatform === 'x' ? (
          <>Fallback chain: X Title \u2192 Social Title \u2192 Post Title &nbsp;|&nbsp; X Image \u2192 Social Image \u2192 Hero</>
        ) : activePlatform === 'pinterest' ? (
          <>Fallback chain: Pinterest Image \u2192 Social Image \u2192 Hero &nbsp;|&nbsp; Best at 1000\u00d71500 (2:3 vertical)</>
        ) : (
          <>Fallback chain: Social Title \u2192 Post Title &nbsp;|&nbsp; Social Image \u2192 Hero Image</>
        )}
      </div>
    </div>
  );
};
